/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useState } from 'react';
import ReactSelect, {
  MenuPlacement,
  MultiValue,
  SingleValue,
  SingleValueProps,
  components,
  ClearIndicatorProps,
} from 'react-select';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { useController, FieldValues, FieldPath, Control } from 'react-hook-form';

import { useTranslation } from 'react-i18next';
import Icon, { IconName } from 'src/components/ui/Icon/Icon';
import styles from './Select.module.scss';
import Badge from '../Badge/Badge';

export type Option = {
  label: string;
  value: string;
  icon?: IconName;
  dot?: boolean;
  isDotFilled?: boolean;
  isDisabled?: boolean;
};

export type SelectProps = {
  options: Option[];
  // defaultValue?: Option | Option[];
  label?: string;
  /**
   * DOM id of react-select's inner input, so an external `<label htmlFor>` can
   * address it. Without this a label can only be associated by wrapping the
   * control, which drags neighbouring hint text into the label as well.
   */
  id?: string;
  // onChange?: (selectedOption: Option | Option[]) => void;
  isMenuOpen?: boolean;
  icon?: IconName;
  fullWidth?: boolean;
  hasDot?: boolean;
  isDotFilled?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  isSearchable?: boolean;
  onInputChange?: (inputValue: string) => void;
  disableLocalFilter?: boolean;
  isInfoDisplay?: boolean;
  isClearable?: boolean;
  menuPlacement?: MenuPlacement;
  menuFixed?: boolean;
  // isMultiSelect?: boolean;
} & (
  | {
      isMultiSelect: true;
      defaultValue?: Option[];
      onChange?: (selectedOption: Option[]) => void;
    }
  | {
      isMultiSelect?: false;
      defaultValue?: Option;
      onChange?: (selectedOption: Option) => void;
    }
);

const SingleValueComponent = ({ children, ...props }: SingleValueProps<Option>) => {
  return (
    <components.SingleValue
      {...props}
      className={`singleValue flex justify-center items-center ${props.data.icon ? 'singleValueIcon' : ''}`}>
      {props.data.icon && <Icon name={props.data.icon} />}
      {props.data.dot && <Badge label="" showDot dotFilled={props.data.isDotFilled || false} />}
      {children}
    </components.SingleValue>
  );
};

const Select: React.FC<SelectProps> = ({
  options,
  defaultValue,
  label,
  id,
  onChange,
  onInputChange,
  isMenuOpen,
  icon,
  fullWidth,
  hasDot,
  isDotFilled,
  placeholder,
  disabled,
  className,
  isSearchable = false,
  isClearable,
  isInfoDisplay,
  isMultiSelect,
  menuPlacement,
  menuFixed,
  disableLocalFilter,
}) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<Option | Option[] | null>(defaultValue || null);

  useEffect(() => {
    setSelectedOption(defaultValue || null);
  }, [disabled, defaultValue]);

  const updatedOptions = options.map(option => ({
    ...option,
    icon: icon || undefined,
  }));

  if (!isMultiSelect) {
    const def = defaultValue as Option;
    if (def && icon) {
      def.icon = icon;
    }

    if (def && hasDot) {
      def.dot = hasDot;
    }

    if (def && isDotFilled) {
      def.isDotFilled = isDotFilled;
    }
  }

  const onSelectChange = (selectedOption: SingleValue<Option> | MultiValue<Option>) => {
    setSelectedOption(selectedOption as Option | Option[]);
    if (onChange) {
      if (isMultiSelect) {
        onChange(selectedOption as Option[]);
      } else {
        onChange(selectedOption as Option);
      }
    }
  };

  const handleInputChange = debounce((inputValue: string) => {
    if (onInputChange) {
      onInputChange(inputValue);
    }
  }, 300);

  return (
    <div
      className={classNames(styles.root, className, {
        [styles.infoDisplay]: isInfoDisplay,
        [styles.isMultiSelect]: isMultiSelect,
      })}>
      {label && <p className="typo-text-14--bold text-faded mb-12">{label}</p>}
      <div className={`${fullWidth ? 'selectContainerFull' : 'selectContainer'}`}>
        <ReactSelect
          inputId={id}
          menuPlacement={menuPlacement}
          menuPosition={menuFixed ? 'fixed' : 'absolute'}
          closeMenuOnScroll={
            menuFixed
              ? (event: Event) => {
                  const target = event.target as HTMLElement | null;
                  return !target?.closest?.('.select__menu-list');
                }
              : undefined
          }
          isDisabled={disabled}
          defaultValue={defaultValue}
          value={selectedOption}
          isMulti={isMultiSelect}
          onChange={option => onSelectChange(option)}
          onInputChange={handleInputChange}
          placeholder={placeholder || t('general.select')}
          styles={{ menu: base => ({ ...base, zIndex: 9999 }) }}
          isClearable={isClearable}
          options={updatedOptions}
          classNamePrefix="select"
          filterOption={disableLocalFilter ? () => true : undefined}
          isSearchable={isSearchable}
          menuIsOpen={isMenuOpen}
          components={{
            SingleValue: SingleValueComponent,
            ClearIndicator: ({ innerProps, clearValue, ...rest }: ClearIndicatorProps<Option, boolean>) => (
              <components.ClearIndicator
                innerProps={{
                  ...innerProps,
                  onMouseDown: e => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (clearValue) {
                      clearValue();
                    }
                  },
                }}
                clearValue={clearValue}
                {...rest}
              />
            ),
          }}
        />
      </div>
    </div>
  );
};

export type ControlledSelectProps<T extends FieldValues> = {
  name: FieldPath<T>;
  containerClassName?: string;
  control: Control<T>;
} & Omit<SelectProps, 'defaultValue' | 'onChange'>;

export const ControlledSelect = <T extends FieldValues>({
  name,
  control,
  options,
  label,
  id,
  isMenuOpen,
  icon,
  fullWidth,
  hasDot,
  isDotFilled,
  placeholder,
  disabled,
  className,
  isSearchable = false,
  isClearable,
  containerClassName,
  isInfoDisplay,
  isMultiSelect,
}: ControlledSelectProps<T>) => {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name, control });

  // Map value to the correct selected option(s)
  const selectedValue = isMultiSelect
    ? // For multi-select, selectedValue must be Option[]
      options.filter(option => (value || ([] as string[])).includes(option.value))
    : // For single-select, selectedValue must be Option | undefined
      options.find(option => value && option.value && option.value === value.toString());

  // Construct props based on whether isMultiSelect is true or false
  const multiProps = isMultiSelect
    ? {
        isMultiSelect: true as const,
        defaultValue: selectedValue as Option[] | undefined,
        onChange: (selectedOption: Option[]) => {
          // Here selectedOption must be an array of Option (multi-select)
          onChange(selectedOption.map(o => o.value));
        },
      }
    : {
        // For single select, we either omit isMultiSelect or set it to false as const.
        // Since the second branch expects isMultiSelect?: false,
        // providing isMultiSelect: false as const is valid.
        isMultiSelect: false as const,
        defaultValue: selectedValue as Option | undefined,
        onChange: (selectedOption: Option) => {
          // Here selectedOption must be a single Option (single-select)
          onChange(selectedOption?.value);
        },
      };

  return (
    <div className={classNames(styles.root, containerClassName)}>
      <Select
        options={options}
        label={label}
        id={id}
        isMenuOpen={isMenuOpen}
        icon={icon}
        fullWidth={fullWidth}
        hasDot={hasDot}
        isDotFilled={isDotFilled}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        isSearchable={isSearchable}
        isClearable={isClearable}
        isInfoDisplay={isInfoDisplay}
        {...multiProps} // Spread the correctly typed props
      />
      {error && (
        <div className="flex align-center gap-x-4 mt-8 status-error-full">
          <Icon name="error" width={16} height={16} />
          <p className="typo-text-12--bold">{error.message}</p>
        </div>
      )}
    </div>
  );
};

export default Select;
